"use client";
import AuthRedirectGuard from "@/components/auth/AuthRedirectGuard";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import Button from "@/components/ui/button/Button";
import { MailIcon } from "@/icons";
import Link from "next/link";
import React, { useState } from "react";

export default function SignInForm() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await sendMagicLink();
  };

  const sendMagicLink = async () => {
    if (!email) return;

    setIsLoading(true);
    
    try {
      const response = await fetch('/api/magiclink/initiate', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ 
          email 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      setIsSent(true);
    } catch (error) {
      console.error('Error sending magic link:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthRedirectGuard>
      <div className="flex flex-col flex-1 lg:w-1/2 w-full">
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <div>
            <div className="mb-8 text-center">
              <div className="mx-auto w-16 h-16 bg-brand-100 dark:bg-brand-900 rounded-full flex items-center justify-center mb-4">
                <MailIcon className="w-8 h-8 text-brand-600 dark:text-brand-400" />
              </div>
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                {isSent ? "Check your email" : "Sign in to your account"}
              </h1>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isSent 
                    ? `We've sent a magic link to ${email}. Click the link to sign in.`
                    : "Enter your email address and we'll send you a magic link to sign in."
                  }
                </p>
            </div>

            {!isSent ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label>
                    Email Address <span className="text-error-500">*</span>
                  </Label>
                  <Input 
                    type="email" 
                    placeholder="Enter your email address"
                    defaultValue={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                
                <Button 
                  className="w-full" 
                  size="sm"
                  disabled={isLoading || !email}
                  onClick={sendMagicLink}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending magic link...
                    </div>
                  ) : (
                    "Send magic link"
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center justify-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        Magic link sent successfully!
                      </p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={() => setIsSent(false)}
                  className="w-full" 
                  size="sm"
                  variant="outline"
                >
                  Send to a different email
                </Button>
              </div>
            )}

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Don&#39;t have an account?{" "}
                <Link
                  href="/signup"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400 font-medium"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthRedirectGuard>
  );
}
